import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  Award, 
  Trophy, 
  Target, 
  Star, 
  ArrowRight, 
  CheckCircle2, 
  AlertTriangle, 
  Brain, 
  Book, 
  Code, 
  Briefcase, 
  LineChart, 
  Clock, 
  BarChart as ChartBar, 
  Lightbulb,
  Users,
  Gauge,
  Zap,
  Play,
  Info
} from 'lucide-react';
import Button from '../components/ui/Button';
import SkillVisualization from '../components/skills/SkillVisualization';
import SkillDistribution from '../components/skills/SkillDistribution';
import MarketDemandSkills from '../components/skills/MarketDemandSkills';
import CurrentSkills from '../components/skills/CurrentSkills';
import SkillChallengeExperience from '../components/challenges/SkillChallengeExperience';

// Mock data for skill distribution
const skillDistributionData = [
  {
    category: 'Technical',
    percentage: 40,
    icon: <Code className="h-5 w-5 text-blue-600" />,
    color: 'text-blue-600'
  },
  {
    category: 'Soft Skills',
    percentage: 25,
    icon: <Brain className="h-5 w-5 text-purple-600" />,
    color: 'text-purple-600'
  },
  {
    category: 'Leadership',
    percentage: 20,
    icon: <Users className="h-5 w-5 text-green-600" />,
    color: 'text-green-600'
  },
  {
    category: 'Domain',
    percentage: 15,
    icon: <Briefcase className="h-5 w-5 text-orange-600" />,
    color: 'text-orange-600'
  }
];

// Mock data for market demand skills
const marketDemandData = [
  { name: 'Machine Learning', demand: 'High', growth: 85 },
  { name: 'Cloud Architecture', demand: 'High', growth: 80 },
  { name: 'Data Analysis', demand: 'High', growth: 75 },
  { name: 'DevOps', demand: 'Medium', growth: 65 },
  { name: 'UI/UX Design', demand: 'Medium', growth: 60 }
];

// Mock data for current skills
const currentSkillsData = [
  { 
    name: 'JavaScript', 
    level: 85, 
    lastPracticed: '2 days ago',
    trend: 'up' 
  },
  { 
    name: 'Python', 
    level: 70, 
    lastPracticed: '1 week ago',
    trend: 'up' 
  },
  { 
    name: 'React', 
    level: 90, 
    lastPracticed: 'Today',
    trend: 'up' 
  }
];

// Mock data for user skills
const mockUserSkills = [
  { id: 1, skill_name: 'JavaScript', proficiency_level: 'Advanced' },
  { id: 2, skill_name: 'React', proficiency_level: 'Intermediate' },
  { id: 3, skill_name: 'Python', proficiency_level: 'Beginner' },
  { id: 4, skill_name: 'SQL', proficiency_level: 'Advanced' },
  { id: 5, skill_name: 'Git', proficiency_level: 'Intermediate' }
];

// Mock data for market skills
const mockMarketSkills = [
  { id: 1, name: 'JavaScript', level: 'Advanced', demand: 'High' },
  { id: 2, name: 'React', level: 'Advanced', demand: 'Very High' },
  { id: 3, name: 'Python', level: 'Advanced', demand: 'High' },
  { id: 4, name: 'SQL', level: 'Intermediate', demand: 'Medium' },
  { id: 5, name: 'AWS', level: 'Advanced', demand: 'Very High' },
  { id: 6, name: 'TypeScript', level: 'Advanced', demand: 'High' }
];

// Mock data for skill challenges
const skillChallenges = [
  {
    id: 'coding',
    title: 'Coding Challenge',
    description: 'Test your programming skills with real-world coding problems',
    type: 'coding',
    duration: 30,
    points: 100,
    difficulty: 'Intermediate',
    icon: Code,
    color: 'bg-blue-100 text-blue-600',
    requirements: ['JavaScript', 'Python', 'Problem Solving'],
    participants: 1250
  },
  {
    id: 'system-design',
    title: 'System Design Challenge',
    description: 'Design scalable systems and architectures',
    type: 'system_design',
    duration: 45,
    points: 150,
    difficulty: 'Advanced',
    icon: Brain,
    color: 'bg-purple-100 text-purple-600',
    requirements: ['System Architecture', 'Scalability', 'Performance'],
    participants: 850
  },
  {
    id: 'problem-solving',
    title: 'Problem Solving',
    description: 'Solve complex algorithmic and business problems',
    type: 'problem_solving',
    duration: 25,
    points: 80,
    difficulty: 'Intermediate',
    icon: Lightbulb,
    color: 'bg-yellow-100 text-yellow-600',
    requirements: ['Algorithms', 'Data Structures', 'Logic'],
    participants: 2100
  },
  {
    id: 'data-analysis',
    title: 'Data Analysis',
    description: 'Analyze complex datasets and derive insights',
    type: 'data_analysis',
    duration: 40,
    points: 120,
    difficulty: 'Advanced',
    icon: LineChart,
    color: 'bg-green-100 text-green-600',
    requirements: ['Statistics', 'Python', 'Data Visualization'],
    participants: 950
  }
];

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const SkillDevelopment: React.FC = () => {
  const [activeSection, setActiveSection] = useState<'analysis' | 'challenges'>('analysis');
  const [selectedChallenge, setSelectedChallenge] = useState<any | null>(null);
  const [showChallengeModal, setShowChallengeModal] = useState(false);

  const handleStartChallenge = (challenge: any) => {
    setSelectedChallenge(challenge);
    setShowChallengeModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { staggerChildren: 0.1 }
            }
          }}
        >
          {/* Hero Section */}
          <div className="relative bg-gradient-to-b from-primary-900 to-primary-800 py-16 mb-12 rounded-3xl overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-40 -left-40 w-80 h-80 bg-primary-700 rounded-full mix-blend-multiply opacity-20 animate-blob"></div>
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-secondary-700 rounded-full mix-blend-multiply opacity-20 animate-blob animation-delay-2000"></div>
              <div className="absolute top-40 left-1/2 transform -translate-x-1/2 w-80 h-80 bg-accent-700 rounded-full mix-blend-multiply opacity-20 animate-blob animation-delay-4000"></div>
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center">
                  Skill Development Hub
                </h1>
                <p className="text-xl text-primary-100 max-w-2xl mx-auto mb-8">
                  Track your progress, identify skill gaps, and accelerate your professional growth with personalized learning paths.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <Gauge className="h-5 w-5 mr-2" />
                    <span>Skill Analysis</span>
                  </div>
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <Trophy className="h-5 w-5 mr-2" />
                    <span>Skill Challenges</span>
                  </div>
                  <div className="flex items-center bg-white/10 rounded-full px-6 py-3 text-primary-100">
                    <Target className="h-5 w-5 mr-2" />
                    <span>Progress Tracking</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Section Tabs */}
          <div className="flex justify-center gap-6 mb-8">
            <Button
              variant={activeSection === 'analysis' ? 'primary' : 'outline'}
              onClick={() => setActiveSection('analysis')}
              icon={<Gauge className="h-4 w-4" />}
              className="w-[200px] h-12"
            >
              Skill Analysis
            </Button>
            <Button
              variant={activeSection === 'challenges' ? 'primary' : 'outline'}
              onClick={() => setActiveSection('challenges')}
              icon={<Trophy className="h-4 w-4" />}
              className="w-[200px] h-12"
            >
              Skill Challenges
            </Button>
          </div>

          {/* Section Explanation */}
          <motion.div 
            variants={itemVariants}
            className="bg-white rounded-xl shadow-md p-6 mb-8 text-center"
          >
            <div className="flex items-center justify-center mb-4">
              <Info className="h-5 w-5 text-primary-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">
                {activeSection === 'analysis' ? 'Skill Analysis Overview' : 'Skill Challenges Overview'}
              </h2>
            </div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {activeSection === 'analysis' 
                ? 'Track your skill progress, identify gaps, and get insights into market demands. Use this analysis to make informed decisions about your skill development journey.'
                : 'Put your skills to the test with real-world challenges. Complete tasks, earn points, and showcase your practical abilities while learning from hands-on experience.'
              }
            </p>
          </motion.div>

          {activeSection === 'analysis' ? (
            <>
              {/* Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Overall Progress</h3>
                    <TrendingUp className="text-green-500 h-6 w-6" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900">78%</p>
                  <p className="text-sm text-gray-600">Towards target skills</p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Skills Mastered</h3>
                    <Star className="text-yellow-500 h-6 w-6" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900">5</p>
                  <p className="text-sm text-gray-600">Out of 8 target skills</p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Learning Streak</h3>
                    <Target className="text-purple-500 h-6 w-6" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900">15</p>
                  <p className="text-sm text-gray-600">Days of consistent learning</p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Time Invested</h3>
                    <Clock className="text-blue-500 h-6 w-6" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900">45h</p>
                  <p className="text-sm text-gray-600">This month</p>
                </div>
              </div>

              {/* Skill Distribution and Market Demand */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <SkillDistribution skills={skillDistributionData} />
                <MarketDemandSkills skills={marketDemandData} />
              </div>

              {/* Current Skills and Growth Trajectory */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <CurrentSkills skills={currentSkillsData} />
                <SkillVisualization 
                  userSkills={mockUserSkills}
                  marketSkills={mockMarketSkills}
                />
              </div>
            </>
          ) : (
            <>
              {/* Skill Challenges Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {skillChallenges.map((challenge) => {
                  const Icon = challenge.icon;
                  return (
                    <motion.div
                      key={challenge.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileHover={{ y: -5 }}
                      transition={{ duration: 0.2 }}
                      className="bg-white rounded-xl shadow-md overflow-hidden"
                    >
                      <div className="p-6 flex flex-col h-full">
                        <div className="flex items-center justify-between mb-4">
                          <div className={`p-2 rounded-lg ${challenge.color}`}>
                            <Icon className="h-6 w-6" />
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            challenge.difficulty === 'Advanced' 
                              ? 'bg-red-100 text-red-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {challenge.difficulty}
                          </span>
                        </div>

                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {challenge.title}
                        </h3>
                        
                        <p className="text-gray-600 mb-4">
                          {challenge.description}
                        </p>

                        <div className="space-y-3 mb-6">
                          <div className="flex items-center text-sm text-gray-500">
                            <Clock className="h-4 w-4 mr-2" />
                            <span>{challenge.duration} minutes</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <Trophy className="h-4 w-4 mr-2" />
                            <span>{challenge.points} points</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <Users className="h-4 w-4 mr-2" />
                            <span>{challenge.participants.toLocaleString()} participants</span>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 mb-6">
                          {challenge.requirements.map((req, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm"
                            >
                              {req}
                            </span>
                          ))}
                        </div>

                        <Button
                          fullWidth
                          onClick={() => handleStartChallenge(challenge)}
                          icon={<Play className="h-4 w-4" />}
                          className="h-12 mt-auto"
                        >
                          Start Challenge
                        </Button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </>
          )}
        </motion.div>
      </div>

      {/* Challenge Modal */}
      {selectedChallenge && (
        <SkillChallengeExperience
          isOpen={showChallengeModal}
          onClose={() => {
            setShowChallengeModal(false);
            setSelectedChallenge(null);
          }}
          challenge={selectedChallenge}
        />
      )}
    </div>
  );
};

export default SkillDevelopment;